import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
	BeneficiaryReconciliationApplyTargetValue,
	GenericBeneficiaryReconciliationPreflightRow,
} from '../../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';

export type BeneficiaryReconciliationPreflightRowResult =
	| {
			action: 'IGNORE_ITEM';
			onHold?: boolean;
	  }
	| {
			action: 'RESOLVE_PREFLIGHT_ROW';
			carrier?: string;
			cpf?: string;
	  }
	| {
			removePending: true;
	  };

interface PreflightRowModalData {
	pendingChange?: {
		action: string;
		targetValue: BeneficiaryReconciliationApplyTargetValue;
	} | null;
	row: GenericBeneficiaryReconciliationPreflightRow;
}

@Component({
	selector: 'app-beneficiary-reconciliation-preflight-row-modal',
	styleUrls: [
		'./beneficiary-reconciliation-preflight-row-modal.component.scss',
	],
	templateUrl:
		'./beneficiary-reconciliation-preflight-row-modal.component.html',
})
export class BeneficiaryReconciliationPreflightRowModalComponent {
	errorMessage = '';
	form: UntypedFormGroup;

	constructor(
		@Inject(MAT_DIALOG_DATA) readonly data: PreflightRowModalData,
		private readonly dialogRef: MatDialogRef<
			BeneficiaryReconciliationPreflightRowModalComponent,
			BeneficiaryReconciliationPreflightRowResult
		>,
		private readonly fb: UntypedFormBuilder,
	) {
		this.form = this.fb.group({
			carrier: [
				data.pendingChange?.targetValue?.carrier ??
					data.row.data?.carrier ??
					'',
			],
			cpf: [
				data.pendingChange?.targetValue?.cpf ??
					data.row.data?.beneficiaryCpf ??
					'',
			],
		});

		this.form.valueChanges.subscribe(() => {
			this.errorMessage = '';
		});
	}

	get issueLabelList(): string[] {
		const labels = [...this.data.row.issues, ...this.data.row.warnings].map(
			(issue) => this.getIssueLabel(issue.code),
		);

		return Array.from(new Set(labels));
	}

	get issueLabels(): string {
		return this.issueLabelList.join(', ');
	}

	get shouldShowCarrier(): boolean {
		return this.hasIssue([
			'AMBIGUOUS_CARRIER',
			'CARRIER_AMBIGUOUS',
			'MISSING_CARRIER',
		]);
	}

	get shouldShowCpf(): boolean {
		return this.hasIssue(['INVALID_CPF', 'MISSING_CPF']);
	}

	get sourceLabel(): string {
		const source = this.data.row.data?.source;

		if (!source) {
			return '-';
		}

		return [
			source.fileName,
			source.sheetName ? `Aba ${source.sheetName}` : '',
			source.rowNumber ? `Linha ${source.rowNumber}` : '',
		]
			.filter(Boolean)
			.join(' • ');
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onIgnore(): void {
		this.dialogRef.close({ action: 'IGNORE_ITEM' });
	}

	onPlaceOnHold(): void {
		this.dialogRef.close({ action: 'IGNORE_ITEM', onHold: true });
	}

	onRemove(): void {
		this.dialogRef.close({ removePending: true });
	}

	onSave(): void {
		const validationError = this.getValidationError();

		if (validationError) {
			this.errorMessage = validationError;

			return;
		}

		this.dialogRef.close({
			action: 'RESOLVE_PREFLIGHT_ROW',
			carrier:
				this.getStringValue(this.form.get('carrier')?.value) || undefined,
			cpf: this.getStringValue(this.form.get('cpf')?.value) || undefined,
		});
	}

	private getIssueLabel(code: string): string {
		switch (code) {
			case 'INVALID_CPF':
				return 'CPF inválido';
			case 'MISSING_CPF':
				return 'CPF ausente';
			case 'AMBIGUOUS_CARRIER':
			case 'CARRIER_AMBIGUOUS':
				return 'Operadora ambígua';
			case 'MISSING_CARRIER':
				return 'Operadora ausente';
			case 'SUBINVOICE_NOT_FOUND':
				return 'Número da subfatura não encontrado';
			case 'TITULARITY_NOT_IDENTIFIED':
				return 'Titularidade não identificada';
			default:
				return code;
		}
	}

	private getStringValue(value: unknown): string {
		return typeof value === 'string' ? value.trim() : '';
	}

	private getValidationError(): string | null {
		if (
			this.shouldShowCpf &&
			!this.getStringValue(this.form.get('cpf')?.value)
		) {
			return 'Informe o CPF para salvar esta correção na fila.';
		}

		if (
			this.shouldShowCarrier &&
			!this.getStringValue(this.form.get('carrier')?.value)
		) {
			return 'Informe a operadora para salvar esta correção na fila.';
		}

		return null;
	}

	private hasIssue(codes: string[]): boolean {
		const codeSet = new Set(codes);

		return [...this.data.row.issues, ...this.data.row.warnings].some((issue) =>
			codeSet.has(issue.code),
		);
	}
}
